import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import TicketsPage from "@/pages/TicketsPage";
import ReportsPage from "@/pages/ReportsPage";
import ManagedTicketsPage from "@/pages/ManagedTicketsPage";
import TicketDetailPage from "@/pages/TicketDetailPage";
import NewTicketPage from "@/pages/NewTicketPage";
import TicketSettingsPage from "@/pages/TicketSettingsPage";
import ApprovalQueuePage from "@/pages/ApprovalQueuePage";
import AssetsPage from "@/pages/AssetsPage";
import AssetDetailPage from "@/pages/AssetDetailPage";
import AssetReportsPage from "@/pages/AssetReportsPage";
import UserDirectoryPage from "@/pages/UserDirectoryPage";
import UserDetailPage from "@/pages/UserDetailPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import NetworkPage from "@/pages/NetworkPage";
import LoginPage from "@/pages/LoginPage";
import OnboardingPage from "@/pages/OnboardingPage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import KBArticleDetailPage from "@/pages/KBArticleDetailPage";
import PortalLoginPage from "@/pages/PortalLoginPage";
import PortalDashboard from "@/pages/PortalDashboard";
import PortalTicketDetail from "@/pages/PortalTicketDetail";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

// Protected route wrapper with layout
function ProtectedRoute({ children, requireStaff = false }) {
  const { isAuthenticated, isStaff, loading, initialized } = useAuth();
  const location = window.location;

  if (!initialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the intended destination for redirect after login
    const returnUrl = location.pathname + location.search;
    if (returnUrl && returnUrl !== '/' && returnUrl !== '/login') {
      sessionStorage.setItem('returnUrl', returnUrl);
    }
    return <Navigate to="/login" replace />;
  }

  if (requireStaff && !isStaff) {
    return <Navigate to="/" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

function AppRoutes() {
  const { isAuthenticated, loading, initialized } = useAuth();

  if (!initialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public route */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
      } />

      {/* All users (authenticated) */}
      <Route path="/" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />

      {/* IT Staff only routes */}
      <Route path="/tickets" element={
        <ProtectedRoute requireStaff>
          <TicketsPage />
        </ProtectedRoute>
      } />
      <Route path="/tickets/managed" element={
        <ProtectedRoute requireStaff>
          <ManagedTicketsPage />
        </ProtectedRoute>
      } />
      <Route path="/tickets/managed/:ticketId" element={
        <ProtectedRoute requireStaff>
          <TicketDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/tickets/new" element={
        <ProtectedRoute requireStaff>
          <NewTicketPage />
        </ProtectedRoute>
      } />
      <Route path="/tickets/approval" element={
        <ProtectedRoute requireStaff>
          <ApprovalQueuePage />
        </ProtectedRoute>
      } />
      <Route path="/tickets/settings" element={
        <ProtectedRoute requireStaff>
          <TicketSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/tickets/:ticketId" element={
        <ProtectedRoute requireStaff>
          <TicketDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute requireStaff>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/network" element={
        <ProtectedRoute requireStaff>
          <NetworkPage />
        </ProtectedRoute>
      } />
      <Route path="/assets" element={
        <ProtectedRoute requireStaff>
          <AssetsPage />
        </ProtectedRoute>
      } />
      <Route path="/assets/reports" element={
        <ProtectedRoute requireStaff>
          <AssetReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/assets/:assetId" element={
        <ProtectedRoute requireStaff>
          <AssetDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute requireStaff>
          <UserDirectoryPage />
        </ProtectedRoute>
      } />
      <Route path="/users/:email" element={
        <ProtectedRoute requireStaff>
          <UserDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute requireStaff>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/kb" element={
        <ProtectedRoute requireStaff>
          <KnowledgeBasePage />
        </ProtectedRoute>
      } />
      <Route path="/kb/:kbId" element={
        <ProtectedRoute requireStaff>
          <KBArticleDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requireStaff>
          <TicketSettingsPage />
        </ProtectedRoute>
      } />

      {/* Portal routes (public - own auth) */}
      <Route path="/portal/login" element={<PortalLoginPage />} />
      <Route path="/portal" element={<PortalDashboard />} />
      <Route path="/portal/tickets/:ticketId" element={<PortalTicketDetail />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(178, 34, 52, 0.3)',
            color: '#fafafa',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '12px',
          },
        }}
      />
    </div>
  );
}

export default App;
